import { E_AlarmType } from "@/api/enums";
import { E_IsRead, IAlaramCommonData1 } from "@/api/moudules/alarmCenter.types";
import { E_SensorKlass } from "@/api/moudules/sensor.types";
import { isEmptyStringOrNil } from "@/utils/lang";
import { buildGasAlarmHistoryRouter } from "@/views/alarmCenter/equipmentAlarm/gasAlarmHistory__entry";
import { buildGdsAlarmHistoryRouter } from "@/views/alarmCenter/equipmentAlarm/gdsAlarmHistory__entry";
import { buildTempVibAlarmHistoryRouter } from "@/views/alarmCenter/equipmentAlarm/temperatureVibAlarmHistory__entry";
import { buildAngleAlarmHistoryRouter } from "@/views/alarmCenter/sensorAlarm/angleAlarmHistory__entry";
import { buildBatteryAlarmHistoryRouter } from "@/views/alarmCenter/sensorAlarm/batteryAlarmHistory__entry";
import { buildOfflineAlarmHistoryRouter } from "@/views/alarmCenter/sensorAlarm/offlineAlarmHistory__entry";
import _ from "lodash";

export const IsReadOptions = Object.freeze([
    E_IsRead.Yes,
    E_IsRead.No,
])

interface IXYZThresold {
    x: number
    y: number
    z: number
}

function safeShowThresold(c: number, d?: number) {
    let hasC = !_.isNil(c)
    let hasD = !_.isNil(d)
    if (!hasC && !hasD) {
        return
    }
    let values = [
        ...hasC ? [c] : [],
        ...hasD ? [d] : []
    ]
    return values.join(',')
}

function buildAlarmThresold(axis: string, c: IXYZThresold, d?: IXYZThresold) {
    if (!axis) return ''
    let parts = []
    if (axis.search('X') !== -1) {
        let r = safeShowThresold(c.x, d?.x)
        if (r) {
            parts.push(r)
        }
    }
    if (axis.search('Y') !== -1) {
        let r = safeShowThresold(c.y, d?.y)
        if (r) {
            parts.push(r)
        }
    }
    if (axis.search('Z') !== -1) {
        let r = safeShowThresold(c.z, d?.z)
        if (r) {
            parts.push(r)
        }
    }
    return parts.join(', ')
}

function buildAlarmValue(axis: string, x: number, y: number, z: number) {
    if (!axis) return ''
    let parts = []
    if (axis.search('X') !== -1) {
        parts.push(x)
    }
    if (axis.search('Y') !== -1) {
        parts.push(y)
    }
    if (axis.search('Z') !== -1) {
        parts.push(z)
    }
    return parts.join(',')
}

export interface IExtractAlarmInfo {
    alarmValueDisplay: string
    thresholdValueDisplay: string
}


export function extractAlarmInfo(data: IAlaramCommonData1) {
    const {
        azhigh,
        battery,
        envelopeX,
        envelopeY,
        envelopeZ,

        kurtX,
        kurtY,
        kurtZ,

        peakX,
        peakY,
        peakZ,

        slopeX,
        slopeY,
        slopeZ,

        tem,
        vx,
        vy,
        vz,
        xcMax,
        xdMax,
        ycMax,
        ydMax,
        zcMax,
        zdMax,

        rx,
        ry,
        rz,

        // gasCO,
        // gasEx,
        // gasH2S,
        // gasNH3,
        // gasSO2,
        // dust,

        alarmAxis,
        alarmType,
        alarmValue,
    } = data

    let thresold = '/'
    let value = '/'
    let cThresold = { x: xcMax, y: ycMax, z: zcMax }
    let dThresold = { x: xdMax, y: ydMax, z: zdMax }
    switch (alarmType) {
        case E_AlarmType.Vibration:
            value = buildAlarmValue(alarmAxis, vx, vy, vz)
            thresold = buildAlarmThresold(alarmAxis, cThresold, dThresold)
            break
        case E_AlarmType.Steepness:
            value = buildAlarmValue(alarmAxis, kurtX, kurtY, kurtZ)
            thresold = buildAlarmThresold(alarmAxis, cThresold, dThresold)
            break
        case E_AlarmType.Crest:
            value = alarmValue
            thresold = buildAlarmThresold(alarmAxis, cThresold, dThresold)
            break
        case E_AlarmType.Envelope:
            value = buildAlarmValue(alarmAxis, envelopeX, envelopeY, envelopeZ)
            thresold = buildAlarmThresold(alarmAxis, cThresold, dThresold)
            break
        case E_AlarmType.Temperature:
            value = _.toString(tem)
            thresold = safeShowThresold(xcMax, xdMax)
            break
        case E_AlarmType.Voltage:
            value = _.toString(battery)
            thresold = safeShowThresold(xcMax, xdMax)
            break
        case E_AlarmType.Offline:
            break
        case E_AlarmType.Electricity:
            break
        case E_AlarmType.HighFrequencyAcceleration:
            value = _.toString(azhigh)
            thresold = safeShowThresold(xcMax, xdMax)
            break
        case E_AlarmType.Slope:
            value = buildAlarmValue(alarmAxis, slopeX, slopeY, slopeZ)
            thresold = buildAlarmThresold(alarmAxis, cThresold)
            break
        case E_AlarmType.DipAngle:
            value = buildAlarmValue(alarmAxis, rx, ry, rz)
            thresold = buildAlarmThresold(alarmAxis, cThresold, dThresold)
            break
        case E_AlarmType.Co:
            value = _.toString(alarmValue)
            thresold = safeShowThresold(xcMax, xdMax)
            break
        case E_AlarmType.Ex:
            value = _.toString(alarmValue)
            thresold = safeShowThresold(xcMax, xdMax)
            break
        case E_AlarmType.H2s:
            value = _.toString(alarmValue)
            thresold = safeShowThresold(xcMax, xdMax)
            break
        case E_AlarmType.Nh3:
            value = _.toString(alarmValue)
            thresold = safeShowThresold(xcMax, xdMax)
            break
        case E_AlarmType.So2:
            value = _.toString(alarmValue)
            thresold = safeShowThresold(xcMax, xdMax)
            break
        case E_AlarmType.Dust:
            value = _.toString(alarmValue)
            thresold = safeShowThresold(xcMax, xdMax)
            break
    }
    return {
        alarmValueDisplay: value,
        thresholdValueDisplay: thresold,
    }
}


export function globalBuildAlarmHistoryRouter(alarmType: E_AlarmType, params) {
    switch (alarmType) {
        case E_AlarmType.DipAngle:
            return buildAngleAlarmHistoryRouter(params)
        case E_AlarmType.Voltage:
            return buildBatteryAlarmHistoryRouter(params)
        case E_AlarmType.Offline:
            return buildOfflineAlarmHistoryRouter(params)
        case E_AlarmType.Ex:
        case E_AlarmType.H2s:
        case E_AlarmType.Co:
        case E_AlarmType.Nh3:
        case E_AlarmType.So2:
            return buildGasAlarmHistoryRouter(params)
        case E_AlarmType.Dust:
            return buildGdsAlarmHistoryRouter(params)
        default:
            return buildTempVibAlarmHistoryRouter(params)
    }
}

export function globalBuildAlarmHistoryRouterFromSensorType(sensorType: E_SensorKlass, params) {
    switch (sensorType) {
        case E_SensorKlass.GS40:
            return buildGasAlarmHistoryRouter(params)
        case E_SensorKlass.DS40:
            return buildGdsAlarmHistoryRouter(params)
        default:
            return buildTempVibAlarmHistoryRouter(params)
    }
}
